import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Heart, UserPlus, Music, MessageCircle, DollarSign, TrendingUp, Award, Target, Film, Disc3 } from 'lucide-react';
import { API } from '../config/api';

const ICON_MAP = {
  'pen-square': Film,
  'music': Music,
  'film': Film,
  'message-circle': MessageCircle,
  'heart': Heart,
  'user-plus': UserPlus,
  'bell': UserPlus,
  'radio': Radio,
  'disc-3': Disc3,
  'dollar-sign': DollarSign,
  'trending-up': TrendingUp,
  'award': Award,
  'target': Target,
};

function ActivityItem({ event, isNew }) {
  const Icon = ICON_MAP[event.icon] || Heart;
  const handleLink = event.user_handle ? `/u/${event.user_handle}` : '#';
  const timeAgo = getTimeAgo(event.created_at);

  return (
    <div
      data-testid={`activity-item-${event.activity_type}`}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full whitespace-nowrap transition-all duration-500
        ${isNew ? 'bg-violet-600/20 border border-violet-500/30' : 'bg-card/40 border border-border/50'}`}
    >
      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isNew ? 'bg-violet-500/30' : 'bg-muted/50'}`}>
        <Icon className={`w-3 h-3 ${isNew ? 'text-violet-300' : 'text-muted-foreground'}`} />
      </div>
      <Link to={handleLink} className="text-[11px] font-semibold text-foreground hover:text-violet-400 transition-colors truncate max-w-[100px]">
        {event.user_name || 'Someone'}
      </Link>
      <span className="text-[11px] text-muted-foreground">{event.label}</span>
      {event.target_name && (
        <span className="text-[11px] font-medium text-foreground/80 truncate max-w-[80px]">{event.target_name}</span>
      )}
      <span className="text-[9px] text-muted-foreground/50">{timeAgo}</span>
    </div>
  );
}

function getTimeAgo(isoStr) {
  if (!isoStr) return '';
  try {
    const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch {
    return '';
  }
}

export function LiveActivityStrip() {
  const [events, setEvents] = useState([]);
  const [liveUsers, setLiveUsers] = useState([]);
  const scrollRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchActivity = useCallback(async () => {
    try {
      const [evRes, liveRes] = await Promise.all([
        fetch(`${API}/activity/live?limit=20`).catch(() => null),
        fetch(`${API}/activity/live-now`).catch(() => null),
      ]);
      if (evRes?.ok) {
        const data = await evRes.json();
        setEvents(data.events || []);
      }
      if (liveRes?.ok) {
        const data = await liveRes.json();
        setLiveUsers(data.live || []);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchActivity();
    intervalRef.current = setInterval(fetchActivity, 15000);
    return () => clearInterval(intervalRef.current);
  }, [fetchActivity]);

  // Auto-scroll animation
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || events.length < 3) return;
    let animFrame;
    let pos = 0;
    const speed = 0.4;
    const animate = () => {
      pos += speed;
      if (pos >= el.scrollWidth - el.clientWidth) pos = 0;
      el.scrollLeft = pos;
      animFrame = requestAnimationFrame(animate);
    };
    animFrame = requestAnimationFrame(animate);
    const pause = () => cancelAnimationFrame(animFrame);
    const resume = () => { animFrame = requestAnimationFrame(animate); };
    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', resume);
    el.addEventListener('touchstart', pause);
    el.addEventListener('touchend', resume);
    return () => {
      cancelAnimationFrame(animFrame);
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', resume);
      el.removeEventListener('touchstart', pause);
      el.removeEventListener('touchend', resume);
    };
  }, [events]);

  if (events.length === 0 && liveUsers.length === 0) return null;

  return (
    <div data-testid="live-activity-strip" className="mb-3">
      {/* Live Now Users */}
      {liveUsers.length > 0 && (
        <div data-testid="live-now-strip" className="flex items-center gap-2 mb-2 overflow-x-auto scrollbar-hide py-1">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600/20 border border-red-500/30 flex-shrink-0">
            <Radio className="w-3.5 h-3.5 text-red-400 animate-pulse" />
            <span className="text-[11px] font-bold text-red-400 uppercase tracking-wider">Live</span>
          </div>
          {liveUsers.map(u => (
            <Link
              key={u.user_id}
              to={`/u/${u.handle || u.user_id}`}
              data-testid={`live-user-${u.user_id}`}
              className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-600/10 border border-red-500/20 hover:border-red-400/40 transition-colors flex-shrink-0"
            >
              <div className="w-5 h-5 rounded-full bg-muted overflow-hidden ring-2 ring-red-500/50">
                {u.avatar ? (
                  <img src={u.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[9px] font-bold text-muted-foreground">{(u.name || '?')[0]}</div>
                )}
              </div>
              <span className="text-[11px] font-semibold text-foreground">{u.name}</span>
              {u.viewers > 0 && <span className="text-[9px] text-red-400">{u.viewers} watching</span>}
            </Link>
          ))}
        </div>
      )}

      {/* Activity Ticker */}
      {events.length > 0 && (
        <div
          ref={scrollRef}
          className="flex items-center gap-2 overflow-x-auto scrollbar-hide py-1"
        >
          {events.map((ev, i) => (
            <ActivityItem key={`${ev.created_at}-${i}`} event={ev} isNew={i < 3} />
          ))}
        </div>
      )}
    </div>
  );
}

export function RecentActivityFeed({ getAuthHeaders }) {
  const [events, setEvents] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders ? getAuthHeaders() : {};
      const [evRes, activeRes] = await Promise.all([
        fetch(`${API}/activity/live?limit=15`, { headers }).catch(() => null),
        fetch(`${API}/activity/recently-active?limit=8`, { headers }).catch(() => null),
      ]);
      if (evRes?.ok) {
        const d = await evRes.json();
        setEvents(d.events || []);
      }
      if (activeRes?.ok) {
        const d = await activeRes.json();
        setActiveUsers(d.users || []);
      }
    } catch {}
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading || (events.length === 0 && activeUsers.length === 0)) return null;

  return (
    <div data-testid="recent-activity-feed" className="mb-4 p-3 rounded-2xl bg-card/50 border border-border">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
        <h3 className="font-heading font-bold text-xs uppercase tracking-wider text-foreground/80">Recent Activity</h3>
      </div>

      {/* Active Users */}
      {activeUsers.length > 0 && (
        <div className="flex items-center gap-1.5 mb-3 overflow-x-auto scrollbar-hide">
          {activeUsers.map(u => (
            <Link
              key={u.user_id}
              to={`/u/${u.handle || u.user_id}`}
              data-testid={`active-user-${u.user_id}`}
              className="flex-shrink-0 group"
              title={`${u.name} — ${u.last_action}`}
            >
              <div className="w-8 h-8 rounded-full bg-muted overflow-hidden ring-2 ring-green-500/40 group-hover:ring-violet-500/60 transition-all">
                {u.avatar ? (
                  <img src={u.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[9px] font-bold text-muted-foreground">{(u.name || '?')[0]}</div>
                )}
              </div>
            </Link>
          ))}
          <span className="text-[10px] text-green-400 font-medium ml-1">{activeUsers.length} active</span>
        </div>
      )}

      {/* Activity List */}
      <div className="space-y-1.5 max-h-48 overflow-y-auto scrollbar-hide">
        {events.slice(0, 10).map((ev, i) => {
          const Icon = ICON_MAP[ev.icon] || Heart;
          return (
            <div key={`${ev.created_at}-${i}`} className="flex items-center gap-2 text-[11px] py-1">
              <Icon className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <Link to={ev.user_handle ? `/u/${ev.user_handle}` : '#'} className="font-semibold text-foreground hover:text-violet-400 truncate max-w-[90px]">
                {ev.user_name}
              </Link>
              <span className="text-muted-foreground">{ev.label}</span>
              {ev.target_name && <span className="text-foreground/70 truncate max-w-[70px]">{ev.target_name}</span>}
              <span className="text-muted-foreground/40 ml-auto flex-shrink-0">{getTimeAgo(ev.created_at)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function WeeklyGrowthBadge({ userId }) {
  const [growth, setGrowth] = useState(null);

  useEffect(() => {
    if (!userId) return;
    fetch(`${API}/activity/weekly-growth/${userId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setGrowth(d); })
      .catch(() => {});
  }, [userId]);

  if (!growth || (!growth.new_followers && !growth.engagement_received)) return null;

  return (
    <div data-testid="weekly-growth-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-600/15 border border-green-500/25">
      <TrendingUp className="w-3 h-3 text-green-400" />
      <span className="text-[10px] font-semibold text-green-400">
        {growth.growth_label || `+${growth.new_followers} this week`}
      </span>
    </div>
  );
}

export function SocialProofLabel({ contentType, contentId }) {
  const [proof, setProof] = useState(null);

  useEffect(() => {
    if (!contentType || !contentId) return;
    fetch(`${API}/activity/social-proof/${contentType}/${contentId}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.proof) setProof(d.proof); })
      .catch(() => {});
  }, [contentType, contentId]);

  if (!proof) return null;

  return (
    <div data-testid="social-proof-label" className="flex flex-wrap items-center gap-2 mt-1.5">
      {proof.trending_label && (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider
          ${proof.trending_label === 'Trending in DMV' ? 'bg-red-600/20 text-red-400 border border-red-500/30' :
            proof.trending_label === 'Rising Fast' ? 'bg-orange-600/20 text-orange-400 border border-orange-500/30' :
            'bg-amber-600/15 text-amber-400 border border-amber-500/25'}`}>
          <TrendingUp className="w-3 h-3" /> {proof.trending_label}
        </span>
      )}
      {proof.time_label && (
        <span className="text-[10px] font-medium text-cyan-400/80">{proof.time_label}</span>
      )}
      {proof.liker_count_text && (
        <div className="flex items-center gap-1">
          {proof.recent_likers?.length > 0 && (
            <div className="flex -space-x-1.5">
              {proof.recent_likers.map((l, i) => (
                <div key={i} className="w-4 h-4 rounded-full bg-muted overflow-hidden ring-1 ring-background">
                  {l.avatar ? <img src={l.avatar} alt="" className="w-full h-full object-cover" /> :
                    <div className="w-full h-full flex items-center justify-center text-[7px] font-bold">{(l.name || '?')[0]}</div>}
                </div>
              ))}
            </div>
          )}
          <span className="text-[10px] text-muted-foreground">{proof.liker_count_text}</span>
        </div>
      )}
    </div>
  );
}
